import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  Action,
  Alert,
  Badge,
  Card,
  ConfirmModal,
  DateInput,
  Icon,
  Modal,
  OrderLineItem,
  Page,
  RelativeDateTime,
  SimpleSelect,
  TextInput,
} from 'corso-admin-ui';
import api from '~/api';
import Address from '~/components/Address';
import DescriptionList, { Description } from '~/components/DescriptionList';

import { formatter } from 'corso-web-lib';
import ClipboardButton from '~/components/ClipboardButton';
import { customFieldValueTypeFormatter } from '~/components/FormatCustomField';
import { MediaGallery } from '~/components/media/MediaGallery';
import OrderReference from '~/components/OrderReference';
import { useNavigateSearchData } from '~/components/searchTable/useNavigateSearchData';
import VerifyAddressButton from '~/components/VerifyAddressButton';
import LineItemDisclosure from '~/features/claimReview/components/LineItemDisclosure';
import ProductReplacement from '~/features/claimReview/components/resolutionModifiers/ProductReplacement';
import { BenefitMetric } from '~/features/claimReview/components/summary/ClaimLedgerSummary';
import { useRegistration } from '~/features/registrationReview/RegistrationReviewProvider';
import useFulfillmentLocations from '~/features/settings/automations/hooks/useFulfillmentLocations';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import { useCurrency } from '~/hooks/useCurrency';
import { useIsConciergeWidget } from '~/hooks/widgetHooks';
import { useIsWidgetAuth } from '~/providers/AutoAuthProvider';
import { useStoreId } from '~/providers/StoreUserProvider';
import {
  RegistrationUpdate,
  registrationUpdate,
  SelectedVariant,
} from '~/types';
import { RegistrationTagDisplay } from './RegistrationTagDisplay';
import { RegistrationTimeline } from './RegistrationTimeline';

function RegistrationDiscountIncentiveDetail() {
  const registration = useRegistration();
  const { discountIncentive } = registration;
  const currency = useCurrency();
  if (!discountIncentive) return null;

  return (
    <Card>
      <div className="flex flex-col gap-1">
        <Card.Heading>Discount</Card.Heading>
        <Card.Line>
          <div>
            <Badge variant="default">{discountIncentive.code}</Badge>
          </div>
          <ClipboardButton
            onClick={() => discountIncentive.code}
            variant="ghost"
          />
        </Card.Line>
      </div>

      {discountIncentive.expiresOn && !discountIncentive.redeemedOn && (
        <div className="flex flex-col gap-1">
          <Card.Heading>Expires </Card.Heading>
          <RelativeDateTime
            dateTime={discountIncentive.expiresOn}
            className="text-xs text-neutral-500"
          />
        </div>
      )}

      {discountIncentive.redeemedOn && (
        <div className="flex flex-col gap-1">
          <Card.Heading>Redeemed</Card.Heading>
          <RelativeDateTime
            dateTime={discountIncentive.redeemedOn}
            className="text-xs text-neutral-500"
          />
        </div>
      )}

      {discountIncentive.orderNo &&
        discountIncentive.platformAdminUrl &&
        discountIncentive.orderCreatedOn && (
          <OrderReference
            heading="Order"
            orderNo={discountIncentive.orderNo}
            platformAdminUrl={discountIncentive.platformAdminUrl}
            createdOn={discountIncentive.orderCreatedOn}
          />
        )}

      {discountIncentive.orderTotal !== undefined && (
        <BenefitMetric
          amount={discountIncentive.orderTotal}
          currencyCode={currency.currencyCode}
          label="New Revenue"
        />
      )}
    </Card>
  );
}

function RegistrationClaimSummary({
  storeId,
  claims,
}: {
  storeId: string;
  claims: CrewMerchantUi.Registration['claims'];
}) {
  return (
    claims.length > 0 &&
    claims.map((claim) => {
      const { externalId, claimType, id: claimId } = claim;

      return (
        <Card key={claimId}>
          <div className="flex flex-col gap-1">
            <Card.Line>
              <Card.Heading>{`${claimType}`}</Card.Heading>
            </Card.Line>
            <div>
              <Action
                className="text-xs"
                accessibilityLabel={`View ${claimType} Claim Details`}
                variant="link"
                to={`/${storeId}/claims/${claimType}/${claimId}`}
              >
                #{`${externalId}`}
              </Action>
            </div>
          </div>
        </Card>
      );
    })
  );
}

const useRegistrationUpdateMutation = () => {
  const storeId = useStoreId();

  const registration = useRegistration();

  return useMutation({
    mutationFn: api.store(storeId).registrations.update,
    onSuccess: () => registration.refetch(),
  });
};

function EditRegistration({
  show,
  registration,
  onClose,
}: {
  registration: CrewMerchantUi.Registration;
  show: boolean;
  onClose: () => void;
}) {
  const { mutate: updateRegistration, isPending } =
    useRegistrationUpdateMutation();

  const { data: registrationSettings } = useConfigSettings(
    ({ registrations }) => registrations,
  );

  const salesChannelOptions =
    registrationSettings?.salesChannelOptions.map((channel) => ({
      label: channel.name,
      value: `${channel.id}`,
    })) ?? [];

  const { handleSubmit, control, reset } = useForm<RegistrationUpdate>({
    values: {
      estimatedPurchaseDate: registration.estimatedPurchaseDate,
      storeSalesChannelId: registration.storeSalesChannelId,
    },
    resolver: zodResolver(registrationUpdate),
  });

  const closeAndReset = () => {
    onClose();
    reset();
  };

  const formId = 'edit-registration-form';
  return (
    <Modal
      title="Edit Registration"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Action>
        </>
      }
    >
      <form
        id={formId}
        // oxlint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit((body) => {
          updateRegistration({
            registrationId: `${registration.id}`,
            body,
          });
          onClose();
        })}
        className="flex flex-col gap-4"
      >
        {salesChannelOptions.length > 0 && (
          <Controller
            control={control}
            name="storeSalesChannelId"
            render={({ field: { onChange, value } }) => (
              <SimpleSelect
                label="Sales Channel"
                options={salesChannelOptions}
                value={value ? `${value}` : undefined}
                onChange={(v) => onChange(Number(v))}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="estimatedPurchaseDate"
          render={({ field, fieldState }) => (
            <DateInput
              id="estimated-purchase-date"
              label="Estimated Purchase Date"
              value={(field.value as string | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
              onChange={field.onChange}
              error={fieldState.error?.message}
              details={
                !(field.value as string | undefined) &&
                'Using Store Defaults for Warranty Reviews'
              } // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
            />
          )}
        />
      </form>
    </Modal>
  );
}

function EditRegistrationAddress({
  show,
  registration,
  onClose,
}: {
  registration: CrewMerchantUi.Registration;
  show: boolean;
  onClose: () => void;
}) {
  const { mutate: updateRegistration, isPending } =
    useRegistrationUpdateMutation();

  const {
    formState: { errors },
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
  } = useForm<RegistrationUpdate>({
    values: {
      address: registration.address,
    },
    resolver: zodResolver(registrationUpdate),
  });

  const registrationHasClaims = registration.claims.length > 0;

  const closeAndReset = () => {
    onClose();
    reset();
  };

  const formId = 'edit-registration-form';
  return (
    <Modal
      title="Edit Customer"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Action>
        </>
      }
    >
      <form
        id={formId}
        // oxlint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit((body) => {
          updateRegistration({
            registrationId: `${registration.id}`,
            body,
          });
          onClose();
        })}
        className="flex flex-col gap-4"
      >
        {registrationHasClaims && (
          <Alert variant="warning" heading="Existing Claim">
            Address changes will not be reflected on existing claims.
          </Alert>
        )}

        <TextInput
          id="shipping-address-first-name"
          label="First Name"
          autoComplete="given-name"
          {...register('address.firstName')}
          error={errors.address?.firstName?.message}
        />
        <TextInput
          id="shipping-address-last-name"
          label="Last Name"
          autoComplete="family-name"
          {...register('address.lastName')}
          error={errors.address?.lastName?.message}
        />

        <TextInput
          id="shipping-address-line1"
          label="Street Address Line 1"
          autoComplete="address-line1"
          {...register('address.line1')}
          error={errors.address?.line1?.message}
        />

        <TextInput
          id="shipping-address-line2"
          label="Street Address Line 2"
          autoComplete="address-line2"
          {...register('address.line2')}
          error={errors.address?.line2?.message}
        />

        <TextInput
          id="shipping-address-city"
          label="City"
          autoComplete="address-level2"
          {...register('address.city')}
          error={errors.address?.city?.message}
        />

        <TextInput
          id="shipping-address-state-or-province"
          label="State or Province Code"
          autoComplete="address-level1"
          {...register('address.stateOrProvinceCode')}
          error={errors.address?.stateOrProvinceCode?.message}
        />

        <TextInput
          id="shipping-address-postal-code"
          label="Postal Code"
          autoComplete="postal-code"
          {...register('address.postalCode')}
          error={errors.address?.postalCode?.message}
        />

        <TextInput
          id="shipping-address-country"
          label="Country Code"
          autoComplete="country"
          {...register('address.countryCode')}
          error={errors.address?.countryCode?.message}
        />

        <VerifyAddressButton
          address={{
            name: `${watch('address.firstName') ?? ''} ${watch('address.lastName') ?? ''}`.trim(),
            line1: watch('address.line1') ?? '',
            line2: watch('address.line2'),
            city: watch('address.city') ?? '',
            stateOrProvinceCode: watch('address.stateOrProvinceCode'),
            postalCode: watch('address.postalCode') ?? '',
            countryCode: watch('address.countryCode') ?? '',
          }}
          onApplyCorrections={(corrected) => {
            if (corrected.line1) setValue('address.line1', corrected.line1);
            if (corrected.line2) setValue('address.line2', corrected.line2);
            if (corrected.city) setValue('address.city', corrected.city);
            if (corrected.postalCode)
              setValue('address.postalCode', corrected.postalCode);
            if (corrected.stateOrProvinceCode)
              setValue(
                'address.stateOrProvinceCode',
                corrected.stateOrProvinceCode,
              );
            if (corrected.countryCode)
              setValue('address.countryCode', corrected.countryCode);
          }}
        />
      </form>
    </Modal>
  );
}

function DeleteRegistrationProduct({
  show,
  lineItemId,
  registrationId,
  onClose,
}: {
  lineItemId: number;
  registrationId: number;
  show: boolean;
  onClose: () => void;
}) {
  const storeId = useStoreId();

  const registration = useRegistration();

  const { mutate: updateRegistration, isPending } = useMutation({
    mutationFn: api.store(storeId).registrations.update,
    onSuccess: () => registration.refetch(),
  });

  const confirmDelete = () => {
    updateRegistration({
      registrationId: `${registrationId}`,
      body: {
        lineItemIdToRemove: lineItemId,
      },
    });
    onClose();
  };

  return (
    <ConfirmModal
      title="Delete Product"
      prompt="Are you sure you would like to remove this product from the Registration?"
      confirmText="Delete"
      cancelText="Cancel"
      show={show}
      loading={isPending}
      onConfirm={confirmDelete}
      onCancel={onClose}
    />
  );
}

// TODO: could be consolidated with the other customer cards, I'm just not smart enough to
// TODO: figure out how to do it right now

function RegistrationCustomerDetail({
  registration,
}: {
  registration: CrewMerchantUi.Registration;
}) {
  const { email, firstName, lastName, address } = registration;
  const [showEditAddress, setShowEditAddress] = useState(false);

  const customerName =
    firstName && lastName ? `${firstName} ${lastName}` : 'No name';

  return (
    <>
      <Card key={registration.id}>
        <div className="flex flex-col gap-1.5">
          <Card.Line>
            <Card.Heading>Customer</Card.Heading>

            <Action
              icon="edit"
              variant="ghost"
              accessibilityLabel="Edit Registration Address"
              onClick={() => setShowEditAddress(true)}
            />
          </Card.Line>

          <p className="text-xs text-neutral-500">{customerName}</p>
        </div>

        <div className="flex flex-col gap-1">
          <Card.Heading>Contact information</Card.Heading>
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-500">{email}</p>
            <ClipboardButton onClick={() => email} variant="ghost" />
          </div>

          <p className="text-xs text-neutral-500">
            {address?.phone ?? 'No phone'}
          </p>
        </div>
        {address && (
          <div className="flex flex-col gap-1">
            <Card.Heading>Shipping address</Card.Heading>
            <Address address={address} />
          </div>
        )}
      </Card>
      <EditRegistrationAddress
        registration={registration}
        show={showEditAddress}
        onClose={() => setShowEditAddress(false)}
      />
    </>
  );
}

function MarketingConsentBadge({ collected }: { collected: boolean }) {
  return (
    <Badge variant={collected ? 'success' : 'default'} className="mt-1.5">
      <Icon icon={collected ? 'verified' : 'close'} />
      {collected ? 'Collected' : 'Not Collected'}
    </Badge>
  );
}

function RegistrationMarketingConsentDetail({
  registration,
}: {
  registration: CrewMerchantUi.Registration;
}) {
  const {
    emailMarketingConsentCollectedOn,
    wasEmailMarketingConsentUpdatedOnPlatform,
  } = registration;

  return (
    <Card>
      <div className="flex flex-col gap-1">
        <Card.Heading>Marketing Consent</Card.Heading>
        <div className="flex flex-wrap items-center gap-1.5">
          <MarketingConsentBadge
            collected={!!emailMarketingConsentCollectedOn}
          />
          {emailMarketingConsentCollectedOn &&
            wasEmailMarketingConsentUpdatedOnPlatform && (
              <Badge variant="info" className="mt-1.5">
                <Icon icon="email" />
                New Subscriber
              </Badge>
            )}
        </div>
      </div>

      {emailMarketingConsentCollectedOn && (
        <div className="flex flex-col gap-1">
          <Card.Heading>Date</Card.Heading>
          <RelativeDateTime
            dateTime={emailMarketingConsentCollectedOn}
            className="text-xs text-neutral-500"
          />
        </div>
      )}
    </Card>
  );
}

function RegistrationSmartRegistrationDetail({
  registration,
}: {
  registration: CrewMerchantUi.Registration;
}) {
  const { suggestedRegistration, lineItems } = registration;
  if (!suggestedRegistration?.wasExtractionSuccessful) return null;

  const { productMatchMetadata } = suggestedRegistration;
  const suggestedVariants = productMatchMetadata?.suggestedVariants ?? [];

  const suggestedVariantsActuallySelected = lineItems.filter((li) =>
    suggestedVariants.some(
      (sv) => sv.idFromPlatform === li.variantIdFromPlatform,
    ),
  );

  return (
    <div className="relative overflow-hidden rounded-lg">
      <div className="absolute inset-x-0 top-0 h-1 ai-shimmer" />
      <Card>
        <Card.Line>
          <div className="flex items-center gap-2">
            <div className="flex shrink-0 items-center justify-center rounded-md p-1.5 ai-gradient">
              <Icon icon="aiInline" className="size-3.5 text-white" />
            </div>
            <Card.Heading>Smart Registration</Card.Heading>
          </div>
        </Card.Line>

        <p className="text-xs text-neutral-500">
          Details were automatically extracted from proof of purchase.
        </p>

        {suggestedVariants.length > 0 && (
          <div className="flex flex-col gap-1">
            <Card.Heading>Suggested Products</Card.Heading>
            <p className="text-xs text-neutral-500">
              {suggestedVariantsActuallySelected.length} of {lineItems.length}{' '}
              products suggested
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

function AddProductsModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const [replacementItems, setReplacementItems] = useState<SelectedVariant[]>(
    [],
  );

  const locations = useFulfillmentLocations();

  const registration = useRegistration();

  const { mutate: updateRegistration, isPending } =
    useRegistrationUpdateMutation();

  const handleSave = () => {
    updateRegistration({
      registrationId: `${registration.id}`,
      body: {
        storeProductVariants: replacementItems.map((item) => ({
          idFromPlatform: item.idFromPlatform,
          quantity: item.quantity,
        })),
      },
    });
    setReplacementItems([]);
  };

  return (
    <Modal
      onClose={onClose}
      show={show}
      title="Add Products"
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            type="submit"
            variant="primary"
            loading={isPending}
            disabled={!replacementItems.length}
            onClick={() => {
              handleSave();
              onClose();
            }}
          >
            Save
          </Action>
        </>
      }
    >
      <ProductReplacement
        selectedVariants={replacementItems}
        onChange={setReplacementItems}
        defaultSearchTerm=""
        locations={locations.data ?? []}
      />
    </Modal>
  );
}

function ProofOfPurchase({
  registration,
}: {
  registration: CrewMerchantUi.Registration;
}) {
  const files = registration.proofOfPurchaseFileUrls;
  if (!files.length) return null;
  return (
    <LineItemDisclosure
      defaultOpen
      title="Proof of Purchase"
      supportingText={<Badge>{formatter.count(files.length)}</Badge>}
    >
      <MediaGallery
        title="Proof of Purchase"
        items={files.map((src, index) => ({
          src,
          alt: `Proof of Purchase ${index + 1}`,
        }))}
      />
    </LineItemDisclosure>
  );
}

function RegistrationRequest({
  registration,
  onEditRegistration,
}: {
  registration: CrewMerchantUi.Registration;
  onEditRegistration: () => void;
}) {
  const {
    estimatedPurchaseDate,
    lineItems,
    registrationChannelName,

    customerProvidedOrderNumber,
    id: registrationId,
    customFields,
    canceledOn,
  } = registration;

  const gridDescriptions: Description[] = [];

  gridDescriptions.push({
    details: registrationChannelName,
    term: 'Channel',
  });

  if (customerProvidedOrderNumber) {
    gridDescriptions.push({
      details: customerProvidedOrderNumber,
      term: 'Customer Provided Order #',
    });
  }

  if (estimatedPurchaseDate) {
    gridDescriptions.push({
      details: formatter.date(estimatedPurchaseDate),
      term: 'Estimated Purchase Date',
    });
  }

  customFields?.forEach(({ displayName, valueType, value }) => {
    gridDescriptions.push({
      term: displayName,
      details: customFieldValueTypeFormatter[valueType](value),
    });
  });

  if (canceledOn) {
    gridDescriptions.push({
      details: <RelativeDateTime dateTime={canceledOn} full />,
      term: 'Canceled',
    });
  }

  const [lineItemIdToDelete, setLineItemIdToDelete] = useState<number | null>(
    null,
  );
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  const isRegistrationEditable =
    registration.claims.length === 0 && !canceledOn;

  return (
    <>
      <Card>
        <Card.Line>
          <Card.Heading>Registration Summary</Card.Heading>

          <Action
            icon="edit"
            variant="ghost"
            accessibilityLabel="Edit Registration"
            onClick={onEditRegistration}
          />
        </Card.Line>

        <DescriptionList descriptions={gridDescriptions} />

        <ProofOfPurchase registration={registration} />
      </Card>

      <Card>
        <Card.Line>
          <Card.Heading>Products</Card.Heading>

          {isRegistrationEditable && (
            <Action
              icon="add"
              variant="ghost"
              onClick={() => setShowAddProductModal(true)}
              accessibilityLabel="Add Products"
            />
          )}
        </Card.Line>

        {lineItems.map((li) => (
          <div className="flex justify-between" key={li.id}>
            <OrderLineItem
              src={li.imgUrl}
              sku={li.sku}
              title={li.name}
              quantity={li.quantity}
              options={li.optionsFromPlatform}
            />

            {isRegistrationEditable && (
              <div>
                <Action
                  icon="remove"
                  variant="ghost"
                  accessibilityLabel="Remove Product"
                  onClick={() => setLineItemIdToDelete(li.id)}
                />
              </div>
            )}
          </div>
        ))}
      </Card>

      {lineItemIdToDelete !== null && (
        <DeleteRegistrationProduct
          lineItemId={lineItemIdToDelete}
          registrationId={registrationId}
          show
          onClose={() => setLineItemIdToDelete(null)}
        />
      )}

      <AddProductsModal
        show={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
      />
    </>
  );
}

export default function RegistrationPage() {
  const { registrationId } = useParams();
  const storeId = useStoreId();
  const isWidgetAuth = useIsWidgetAuth();
  const registration = useRegistration();
  const isCorsoConcierge = useIsConciergeWidget();

  const { mutate: cancelRegistration, isPending: isCancelPending } =
    useMutation({
      mutationFn: api.store(storeId).registrations.cancel,
      onSuccess: registration.refetch,
    });

  const { mutate: restoreRegistration, isPending: isRestorePending } =
    useMutation({
      mutationFn: api.store(storeId).registrations.restore,
      onSuccess: registration.refetch,
    });

  const [showEditRegistration, setShowEditRegistration] = useState(false);

  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showRestoreConfirmation, setShowRestoreConfirmation] = useState(false);

  const paginationActions = useNavigateSearchData({
    currentId: Number(registrationId),
    kind: 'Registration',
  });

  const { claims, canceledOn } = registration;

  return (
    <Page
      backAction={!isWidgetAuth}
      paginationActions={isCorsoConcierge ? undefined : paginationActions}
      title={`#${registration.externalId}`}
      subtitle={
        <>
          {canceledOn ? 'Canceled' : 'Active'}

          <p>
            Created <RelativeDateTime dateTime={registration.createdOn} full />
          </p>
        </>
      }
      primaryAction={
        canceledOn || isWidgetAuth
          ? undefined
          : {
              to: `/${storeId}/orders/lookup/${registration.orderIdFromPlatform}`,
              content: 'Create Claim',
            }
      }
      secondaryActions={
        canceledOn
          ? [
              {
                id: 'restore-registration',
                content: 'Restore Registration',
                icon: 'undo' as const,
                onAction: () => setShowRestoreConfirmation(true),
              },
            ]
          : [
              {
                id: 'cancel-registration',
                content: 'Cancel Registration',
                icon: 'close' as const,
                onAction: () => setShowCancelConfirmation(true),
              },
            ]
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[3fr_1fr]">
        <div className="space-y-4">
          <RegistrationRequest
            registration={registration}
            onEditRegistration={() => setShowEditRegistration(true)}
          />
          <RegistrationTimeline />
        </div>
        <div className="space-y-4">
          <RegistrationClaimSummary storeId={storeId} claims={claims} />
          <RegistrationCustomerDetail registration={registration} />
          <RegistrationTagDisplay registrationId={registration.id} />

          <RegistrationMarketingConsentDetail registration={registration} />

          <RegistrationSmartRegistrationDetail registration={registration} />
          <RegistrationDiscountIncentiveDetail />
        </div>
      </div>
      <EditRegistration
        registration={registration}
        show={showEditRegistration}
        onClose={() => setShowEditRegistration(false)}
      />
      <ConfirmModal
        onCancel={() => setShowCancelConfirmation(false)}
        title="Cancel Registration"
        prompt={
          <div className="flex flex-col gap-2">
            <p>Are you sure you want to cancel this registration?</p>
            <p>
              Once cancelled, claims can no longer be created for this
              registration, and it will no longer be editable.
            </p>
          </div>
        }
        show={showCancelConfirmation}
        loading={isCancelPending}
        cancelText="Keep Registration"
        confirmText="Cancel"
        onConfirm={() => {
          setShowCancelConfirmation(false);
          cancelRegistration({
            registrationId: `${registration.id}`,
          });
        }}
      />
      <ConfirmModal
        onCancel={() => setShowRestoreConfirmation(false)}
        title="Restore Registration"
        prompt={
          <div className="flex flex-col gap-2">
            <p>Are you sure you want to restore this registration?</p>
            <p>
              Once restored, claims can be created for this registration, and it
              will be editable again.
            </p>
          </div>
        }
        show={showRestoreConfirmation}
        loading={isRestorePending}
        cancelText="Keep Canceled"
        confirmText="Restore"
        onConfirm={() => {
          setShowRestoreConfirmation(false);
          restoreRegistration({
            registrationId: `${registration.id}`,
          });
        }}
      />
    </Page>
  );
}
